import React, {
  useCallback,
  useEffect,
  useState,
  createContext,
  useContext } from
'react';
export type Lang = 'en' | 'ar';
interface LanguageContextValue {
  lang: Lang;
  setLang: (lang: Lang) => void;
  toggleLang: () => void;
  isRTL: boolean;
  t: (
  input:
  {
    en: string;
    ar: string;
  } |
  string |
  undefined |
  null)
  => string;
  ui: (typeof UI_STRINGS)['en'];
}
const STORAGE_KEY = 'ahg.lang';
const AUTO_DETECT_FLAG = 'ahg.lang.auto';
const LanguageContext = createContext<LanguageContextValue | undefined>(
  undefined
);
const ARABIC_SPEAKING_COUNTRIES = new Set([
  'SA',
  'AE',
  'EG',
  'IQ',
  'JO',
  'KW',
  'LB',
  'LY',
  'MA',
  'OM',
  'PS',
  'QA',
  'SD',
  'SY',
  'TN',
  'YE',
  'BH',
  'DZ',
  'DJ',
  'KM',
  'MR',
  'SO',
]);
// Centralised UI chrome strings used across the public site.
const UI_STRINGS = {
  en: {
    // Nav
    discover: 'Discover',
    itineraries: 'Itineraries',
    editorial: 'Editorial',
    practicalInfo: 'Practical Info',
    favorites: 'Favorites',
    search: 'Search',
    menu: 'Menu',
    goToTop: 'Back to top',
    // Generic
    viewAll: 'View all',
    viewDetails: 'View details',
    save: 'Save',
    saved: 'Saved',
    close: 'Close',
    cancel: 'Cancel',
    submit: 'Submit',
    submitting: 'Submitting...',
    edit: 'Suggest Edit',
    directions: 'Directions',
    reservation: 'Reservation',
    whatsapp: 'WhatsApp',
    writeReview: 'Write a Review',
    readMore: 'Read more',
    showMore: 'Show more',
    showLess: 'Show less',
    // Home
    heroTitle: 'Discover the Soul of Alexandria',
    heroSearchPlaceholder: 'Search places, neighborhoods, or cuisines...',
    editorsPicks: "Editor's Picks",
    editorsPicksSub: 'Curated by our editors',
    editorsPick: "Editor's Pick",
    hiddenGem: 'Hidden Gem',
    wanderNeighborhoods: 'Wander the Neighborhoods',
    curatedItineraries: 'Curated Itineraries',
    allItineraries: 'All itineraries',
    placesCount: 'Places',
    // Sidebar / detail
    bestTimeToVisit: 'Best Time to Visit',
    contact: 'Contact',
    accessibility: 'Accessibility',
    wheelchairAccess: 'Wheelchair Access',
    pramFriendly: 'Pram Friendly',
    yes: 'Yes',
    no: 'No',
    insiderTips: 'Insider Tips',
    whyWeLoveItLabel: 'Editor',
    basedOnReviews: 'based on',
    reviewsWord: 'reviews',
    whatVisitorsSay: 'What Visitors Say',
    // Favorites
    favoritesTitle: 'Favorites',
    yourCollection: 'Your Collection',
    favoritesEmpty: 'No favorites yet',
    favoritesEmptyHint:
    'Tap the heart on any place you love and it will appear here for easy access on your next visit.',
    startExploring: 'Start exploring',
    clearAll: 'Clear all',
    savedPlace: 'Saved place',
    savedPlaces: 'Saved places',
    // Search
    searchPlaceholder: 'Search places, neighborhoods, itineraries...',
    startTyping: 'Start typing to search across the guide.',
    noResultsFor: 'No results for',
    differentKeyword: 'Try a different keyword or check spelling.',
    toClose: 'to close',
    result: 'result',
    results: 'results',
    // Group labels
    places: 'Places',
    neighborhoods: 'Neighborhoods'
  },
  ar: {
    discover: 'استكشف',
    itineraries: 'مسارات',
    editorial: 'مقالات',
    practicalInfo: 'معلومات تهمك',
    favorites: 'المفضلة',
    search: 'بحث',
    menu: 'القائمة',
    goToTop: 'العودة لأعلى الصفحة',
    viewAll: 'عرض الكل',
    viewDetails: 'عرض التفاصيل',
    save: 'حفظ',
    saved: 'محفوظ',
    close: 'إغلاق',
    cancel: 'إلغاء',
    submit: 'إرسال',
    submitting: 'جاري الإرسال...',
    edit: 'اقتراح تعديل',
    directions: 'الاتجاهات',
    reservation: 'حجز',
    whatsapp: 'واتساب',
    writeReview: 'اكتب تقييماً',
    readMore: 'اقرأ المزيد',
    showMore: 'عرض المزيد',
    showLess: 'عرض أقل',
    heroTitle: 'اكتشف روح الإسكندرية',
    heroSearchPlaceholder: 'ابحث عن أماكن أو أحياء أو مأكولات...',
    editorsPicks: 'مختارات المحرر',
    editorsPicksSub: 'أفضل الجواهر الخفية',
    editorsPick: 'مختار المحرر',
    hiddenGem: 'جوهرة خفية',
    wanderNeighborhoods: 'تجول في الأحياء',
    curatedItineraries: 'مسارات مقترحة',
    allItineraries: 'كل المسارات',
    placesCount: 'مكان',
    bestTimeToVisit: 'أفضل وقت للزيارة',
    contact: 'تواصل',
    accessibility: 'إمكانية الوصول',
    wheelchairAccess: 'مدخل للكراسي المتحركة',
    pramFriendly: 'مناسب لعربات الأطفال',
    yes: 'نعم',
    no: 'لا',
    insiderTips: 'نصائح من الداخل',
    whyWeLoveItLabel: 'المحرر',
    basedOnReviews: 'بناءً على',
    reviewsWord: 'تقييم',
    whatVisitorsSay: 'ماذا يقول الزوار',
    favoritesTitle: 'المفضلة',
    yourCollection: 'مجموعتك',
    favoritesEmpty: 'لا توجد مفضلة بعد',
    favoritesEmptyHint:
    'اضغط القلب على أي مكان يعجبك وسيظهر هنا للوصول إليه بسهولة في زيارتك القادمة.',
    startExploring: 'ابدأ الاستكشاف',
    clearAll: 'مسح الكل',
    savedPlace: 'مكان محفوظ',
    savedPlaces: 'أماكن محفوظة',
    searchPlaceholder: 'ابحث عن أماكن وأحياء ومسارات...',
    startTyping: 'ابدأ بالكتابة للبحث في الدليل.',
    noResultsFor: 'لا توجد نتائج لـ',
    differentKeyword: 'جرب كلمة مختلفة أو تحقق من الإملاء.',
    toClose: 'للإغلاق',
    result: 'نتيجة',
    results: 'نتائج',
    places: 'الأماكن',
    neighborhoods: 'الأحياء'
  }
} as const;
function readStored(): Lang | null {
  if (typeof window === 'undefined') return null;
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    if (v === 'ar' || v === 'en') return v;
  } catch {}
  return null;
}
function detectFromBrowser(): Lang | null {
  if (typeof navigator === 'undefined') return null;
  const list =
    navigator.languages && navigator.languages.length > 0 ?
    navigator.languages :
    [navigator.language || ''];
  for (const l of list) {
    if (l && l.toLowerCase().startsWith('ar')) return 'ar';
  }
  return null;
}
function hasAutoDetected(): boolean {
  if (typeof window === 'undefined') return true;
  try {
    return window.localStorage.getItem(AUTO_DETECT_FLAG) === '1';
  } catch {
    return true;
  }
}
function markAutoDetected() {
  try {
    window.localStorage.setItem(AUTO_DETECT_FLAG, '1');
  } catch {}
}
export function LanguageProvider({ children }: {children: React.ReactNode;}) {
  const [lang, setLangState] = useState<Lang>(() => {
    const stored = readStored();
    if (stored) {
      markAutoDetected();
      return stored;
    }
    const fromBrowser = detectFromBrowser();
    if (fromBrowser) return fromBrowser;
    return 'en';
  });
  // Optional geo-IP: one-shot per browser; skipped if language already persisted or browser is Arabic
  useEffect(() => {
    if (hasAutoDetected()) return;
    if (detectFromBrowser() === 'ar') {
      markAutoDetected();
      return;
    }
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), 600);
    fetch('https://api.country.is', { signal: controller.signal })
      .then((res) => (res.ok ? res.json() : null))
      .then((data: unknown) => {
        if (
          data &&
          typeof data === 'object' &&
          'country' in data &&
          typeof (data as { country: unknown }).country === 'string'
        ) {
          const code = (data as { country: string }).country.toUpperCase();
          if (ARABIC_SPEAKING_COUNTRIES.has(code)) setLangState('ar');
        }
      })
      .catch(() => {})
      .finally(() => {
        window.clearTimeout(timeoutId);
        markAutoDetected();
      });
    return () => {
      window.clearTimeout(timeoutId);
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount
  }, []);
  // Persist + apply to <html> for global font/dir hooks
  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, lang);
    } catch {}
    if (typeof document !== 'undefined') {
      document.documentElement.lang = lang;
      document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
    }
  }, [lang]);
  const setLang = useCallback((l: Lang) => setLangState(l), []);
  const toggleLang = useCallback(
    () => setLangState((prev) => prev === 'en' ? 'ar' : 'en'),
    []
  );
  const t = useCallback<LanguageContextValue['t']>(
    (input) => {
      if (input == null) return '';
      if (typeof input === 'string') return input;
      return input[lang] ?? input.en ?? '';
    },
    [lang]
  );
  const value: LanguageContextValue = {
    lang,
    setLang,
    toggleLang,
    isRTL: lang === 'ar',
    t,
    ui: UI_STRINGS[lang]
  };
  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>);

}
export function useLanguage(): LanguageContextValue {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    // Safe fallback for components rendered outside the public layout (e.g. admin).
    return {
      lang: 'en',
      setLang: () => {},
      toggleLang: () => {},
      isRTL: false,
      t: (input) =>
      input == null ?
      '' :
      typeof input === 'string' ?
      input :
      input.en ?? '',
      ui: UI_STRINGS.en
    };
  }
  return ctx;
}